import { useEffect, useState } from "react";
import { Form, Flex, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import loginImage from "images/login.png";
import { _isRequired, getPhoneMaskValue, getPhone, renderCompaniesOptions } from "utils";
import { ENDPOINTS } from "services";
import { useStores } from "stores";
import { WhiteBoxBlock, CustomButton, PhoneNumberInput, PasswordInput, SearchSelect, OtpInput } from "ui";
import { SMS_CODE_RETRY_DELAY_IN_SECONDS } from "utils/envConstants";
import styles from "./login.module.css";
import { useNotificationContext } from "providers";
import { CompanyUserDto, NOTIFICATION_TYPE_ENUM } from "interfaces";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { LoginData } from "stores/UserStore";
import { ReturnButton } from "ui";
import { useTimerState } from "./useTimerState";
import { OtpTimeLeftWithContext } from "ui/Inputs/OtpInput/OtpTimeLeftWithContext";

enum AUTH_STEP {
  LOGIN = "LOGIN",
  SMS = "SMS",
  CHANGE_PASSWORD = "CHANGE_PASSWORD",
}

const CODE_ERROR = "invalid_otp_code";

const LoginForm = ({ loading, userCompanies }: { loading: boolean; userCompanies: CompanyUserDto[] }) => {
  return (
    <Flex vertical>
      <Typography.Title level={1}>Добро пожаловать в систему</Typography.Title>
      <PhoneNumberInput
        name="login"
        disabled={loading || userCompanies.length > 1}
        autoComplete="username"
        placeholder="Телефон"
        rules={[_isRequired("Номер телефона обязателен для ввода")]}
      />
      <Form.Item name="password" rules={[_isRequired("Пароль обязателен для ввода")]}>
        <PasswordInput
          placeholder="Пароль"
          disabled={loading || userCompanies.length > 1}
          autoComplete="current-password"
        />
      </Form.Item>

      {userCompanies.length > 1 && (
        <Form.Item
          rules={[_isRequired("Выбор компании обязателен")]}
          name="companyId"
          className="full-width"
          validateDebounce={FORM_VALIDATION_DEBOUNCE}
          children={
            <SearchSelect placeholder="Организация" disabled={loading} loading={loading} size="large">
              {renderCompaniesOptions(userCompanies)}
            </SearchSelect>
          }
        />
      )}
    </Flex>
  );
};

const getTextFromError = (error: any) => {
  if (error === "cert_error") {
    return "Сервис авторизации временно неисправен, попробуйте позже";
  }
  if (error.response.body.error === CODE_ERROR) {
    return "";
  }
  return error.response.body.error_description ?? "Ошибка в сервисе авторизации";
};

export const LoginPage = () => {
  const { user } = useStores();
  const navigate = useNavigate();
  const { openNotification } = useNotificationContext();
  const { timeLeft, startCountdown, finishCountdown } = useTimerState(0);
  const [error, setError] = useState(user.error);
  const [errorCode, setErrorCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [authStep, setAuthStep] = useState(AUTH_STEP.LOGIN);
  const [userCompanies, setUserCompanies] = useState<CompanyUserDto[]>([]);
  const [storedLoginValue, setStoredLoginValue] = useState<any>(null);
  const [storedPasswordValue, setStoredPasswordValue] = useState<string | null>(null);

  const startOtpCountdown = () => {
    const currentLoginValue = form.getFieldValue("login");
    setStoredLoginValue(currentLoginValue);
    const currentPasswordValue = form.getFieldValue("password");
    setStoredPasswordValue(currentPasswordValue);
    startCountdown();

    form.setFieldsValue({
      sms: "",
    });
  };

  const setLastCompanyId = () => {
    const phone = storedLoginValue || form.getFieldValue("login");
    if (!phone || !phone.phoneNumber) {
      return;
    }
    const lastFourDigits = phone.phoneNumber.slice(-4);
    localStorage.setItem(`lastCompany_${lastFourDigits}`, form.getFieldValue("companyId"));
  };

  useEffect(() => {
    if (userCompanies.length === 1) {
      form.setFieldsValue({
        companyId: userCompanies[0].id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCompanies]);

  const sendSms = async () => {
    const values = form.getFieldsValue();

    setLoading(true);
    setError("");

    const login = loginStep ? getPhoneMaskValue(values.login) : getPhoneMaskValue(storedLoginValue);

    const password = loginStep ? values.password : storedPasswordValue;

    try {
      const response = await fetch(ENDPOINTS.loginOtp(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          login,
          password,
        }),
      });
      if (response.ok) {
        const companies = (await response.json()) as CompanyUserDto[];
        const sortedCompanies = [...companies].sort((a, b) => a.companyName.localeCompare(b.companyName));

        const areArraysEqual =
          sortedCompanies.length === userCompanies.length &&
          sortedCompanies.every((company, index) => company.id === userCompanies[index].id);

        if (!areArraysEqual) {
          setUserCompanies(sortedCompanies);
        }
        startOtpCountdown();
        if (sortedCompanies.length <= 1) {
          setAuthStep(AUTH_STEP.SMS);
        }
      } else {
        throw await response.json();
      }
    } catch (e: any) {
      if (e.type === "/otp-error") {
        openNotification({
          description: e.title || `Код можно отправить раз в ${SMS_CODE_RETRY_DELAY_IN_SECONDS} секунд`,
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
      } else {
        setError(e.title || "Неверный логин или пароль");
      }
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    const values = form.getFieldsValue();

    setLoading(true);
    setError("");
    const data: LoginData = {
      user: getPhoneMaskValue(storedLoginValue),
      smsCode: values.sms,
      companyId: form.getFieldValue("companyId") ?? "",
    };

    user
      .login(data)
      .then(() => {
        if (userCompanies.length > 1) {
          setLastCompanyId();
        }

        const redirectPath = user.redirectPathname;
        user.clearRedirectPathname();
        if (redirectPath) {
          navigate(redirectPath);
        }
      })
      .catch((e) => {
        const errorMessage = getTextFromError(e);
        setErrorCode(e.response.body.error === CODE_ERROR ? "Неверный код" : "");
        setError(errorMessage);
      })
      .finally(() => setLoading(false));
  };

  const clearState = () => {
    setError("");
    setErrorCode("");
    setAuthStep(AUTH_STEP.LOGIN);
    setUserCompanies([]);
    setStoredLoginValue(null);
    finishCountdown();

    form.resetFields();
  };

  const handleFormValuesChange = () => {
    if (error) {
      setError("");
    }
    if (errorCode) {
      setErrorCode("");
    }
  };

  const loginStep = authStep === AUTH_STEP.LOGIN;
  const smsStep = authStep === AUTH_STEP.SMS;

  return (
    <>
      <Flex align="center" justify="center" className={styles.loginBox}>
        <WhiteBoxBlock image={{ src: loginImage, alt: "Login" }}>
          <Form
            form={form}
            name="login"
            onFinish={() => {
              if (loginStep && userCompanies.length === 0) {
                sendSms();
              } else if (loginStep) {
                setAuthStep(AUTH_STEP.SMS);
              } else login();
            }}
            onValuesChange={handleFormValuesChange}
            className={styles.loginForm}
          >
            <Flex vertical>
              {loginStep && <LoginForm loading={loading} userCompanies={userCompanies} />}

              {smsStep && (
                <Flex vertical gap="middle">
                  <Flex vertical gap="12px">
                    <ReturnButton onClick={clearState} disabled={loading} loading={loading} className="bg-basic-200" />

                    <Flex vertical gap={8}>
                      <Typography.Title level={1} className="margin-none">
                        Введите код из СМС
                      </Typography.Title>
                      <Typography.Text>
                        Отправлен на номер {getPhone(getPhoneMaskValue(storedLoginValue))}
                      </Typography.Text>
                    </Flex>
                  </Flex>

                  <OtpInput
                    name="sms"
                    disabled={loading}
                    onSendSms={sendSms}
                    loading={loading}
                    onComplete={() => login()}
                    error={errorCode}
                    className=""
                  />
                </Flex>
              )}
              {error && (
                <Flex justify="center" className="ant-form-item-explain-error">
                  {error}
                </Flex>
              )}
              <Form.Item className="margin-bottom-sm">
                <CustomButton htmlType="submit" size="xlarge" fullWidth loading={loading}>
                  {loginStep && userCompanies.length === 0 ? "Войти" : "Продолжить"}
                </CustomButton>
              </Form.Item>
              {smsStep && <OtpTimeLeftWithContext onResend={sendSms} loading={loading} timeLeft={timeLeft} />}
            </Flex>
          </Form>
        </WhiteBoxBlock>
      </Flex>
    </>
  );
};
